/**
 * Performance Monitor للنظام الديناميكي
 * يراقب أداء العرض ويحذر من البطء
 */

import React from 'react';

interface PerformanceMetric {
  sectionKey: string;
  renderTime: number;
  contentSize: number;
  timestamp: number;
  hasIssues: boolean;
}

interface PerformanceReport {
  avgRenderTime: number;
  slowestSection: { key: string; time: number } | null;
  totalSections: number;
  warnings: string[];
  recommendations: string[];
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private readonly MAX_RENDER_TIME = 40; // ms - حسب التعليمات
  private readonly WARNING_SIZE = 100000; // bytes - 100KB warning threshold

  startTimer(sectionKey: string): () => void {
    const startTime = performance.now();
    
    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      
      this.recordMetric({
        sectionKey,
        renderTime,
        contentSize: 0, // سيتم تحديثه لاحقاً
        timestamp: Date.now(),
        hasIssues: renderTime > this.MAX_RENDER_TIME
      });
      
      if (renderTime > this.MAX_RENDER_TIME) {
        console.warn(`⚠️ Performance: Section '${sectionKey}' took ${renderTime.toFixed(2)}ms (>40ms threshold)`);
      }
    };
  }

  recordMetric(metric: PerformanceMetric) {
    this.metrics.push(metric);
    
    // الاحتفاظ بآخر 50 قياس فقط
    if (this.metrics.length > 50) {
      this.metrics = this.metrics.slice(-50);
    }
  }

  generateReport(): PerformanceReport {
    if (this.metrics.length === 0) {
      return {
        avgRenderTime: 0,
        slowestSection: null,
        totalSections: 0,
        warnings: ['لا توجد قياسات أداء متاحة'],
        recommendations: []
      };
    }

    const avgRenderTime = this.metrics.reduce((sum, m) => sum + m.renderTime, 0) / this.metrics.length;
    const slowestSection = this.metrics.reduce((prev, current) => 
      current.renderTime > (prev?.renderTime || 0) ? current : prev
    );
    
    const warnings: string[] = [];
    const recommendations: string[] = [];
    
    // تحليل المشاكل
    const slowSections = this.metrics.filter(m => m.renderTime > this.MAX_RENDER_TIME);
    if (slowSections.length > 0) {
      warnings.push(`${slowSections.length} أقسام تتجاوز حد الـ ${this.MAX_RENDER_TIME}ms`);
      recommendations.push('فحص المحتوى الديناميكي والصور الكبيرة في الأقسام البطيئة');
    }
    
    if (avgRenderTime > this.MAX_RENDER_TIME) {
      warnings.push(`متوسط وقت العرض ${avgRenderTime.toFixed(2)}ms يتجاوز الحد المسموح`);
      recommendations.push('تحسين عام للأداء مطلوب');
    }

    // تحليل الاتجاهات
    const recentMetrics = this.metrics.slice(-10);
    const recentAvg = recentMetrics.reduce((sum, m) => sum + m.renderTime, 0) / recentMetrics.length;
    const olderMetrics = this.metrics.slice(-20, -10);
    const olderAvg = olderMetrics.length > 0 ? 
      olderMetrics.reduce((sum, m) => sum + m.renderTime, 0) / olderMetrics.length : recentAvg;
    
    if (recentAvg > olderAvg * 1.2) {
      warnings.push('تدهور في الأداء مؤخراً');
      recommendations.push('مراجعة التغييرات الأخيرة للمحتوى');
    }

    return {
      avgRenderTime,
      slowestSection: slowestSection ? { key: slowestSection.sectionKey, time: slowestSection.renderTime } : null,
      totalSections: new Set(this.metrics.map(m => m.sectionKey)).size,
      warnings,
      recommendations
    };
  }

  // مراقبة خاصة للـ Dynamic Blocks
  monitorDynamicBlock(entity: string, limit: number, fetchTime: number) {
    if (fetchTime > 2000) { // 2 seconds warning
      console.warn(`🐌 Dynamic Block: ${entity} fetch took ${fetchTime}ms (>2s)`);
    }
    
    if (limit > 20) {
      console.warn(`📊 Dynamic Block: ${entity} limit=${limit} might be too high (recommended: ≤20)`);
    }
  }

  // تقرير مبسط للـ Console
  logSummary() {
    const report = this.generateReport();
    console.group('📊 Performance Summary');
    console.log(`Average render time: ${report.avgRenderTime.toFixed(2)}ms`);
    if (report.slowestSection) {
      console.log(`Slowest section: ${report.slowestSection.key} (${report.slowestSection.time.toFixed(2)}ms)`);
    }
    if (report.warnings.length > 0) {
      console.warn('Warnings:', report.warnings);
    }
    if (report.recommendations.length > 0) {
      console.info('Recommendations:', report.recommendations);
    }
    console.groupEnd();
  }

  // إعادة تعيين المقاييس
  reset() {
    this.metrics = [];
    console.log('🔄 Performance metrics reset');
  }
}

// مثيل عام
export const performanceMonitor = new PerformanceMonitor();

// Hook لسهولة الاستخدام
export function usePerformanceTimer(sectionKey: string) {
  return React.useCallback(() => {
    return performanceMonitor.startTimer(sectionKey);
  }, [sectionKey]);
}

// Export للاستخدام المباشر
export { PerformanceMonitor };
export type { PerformanceMetric, PerformanceReport };